/**
 * NetLink — o lado cliente do relay (§13.3/§13.4).
 *
 * Interface única `send/on*` no padrão do Pixel Paddle Duel: hoje só o
 * RelayTransport (WebSocket); o RTCTransport (DataChannel P2P com fallback
 * automático) entra por baixo desta MESMA interface no M8 — a GameScene nunca
 * saberá qual back-end está ativo.
 *
 * URL: produção deriva de location (wss://host/pixelraid-ws/); dev usa
 * ws://localhost:3021; ?ws= sobrepõe (testes).
 *
 * P2P (§13.3): após a sala fechar o elenco, cada PAR de peers negocia um
 * DataChannel WebRTC (malha — ≤4 jogadores = ≤6 pares; o lado de menor
 * peerId oferece). Sinalização viaja pelo relay em `d.k = rtc-*`. A partida
 * COMEÇA no relay e migra por par quando o canal abre; falha/timeout/queda
 * fica/volta ao relay POR PAR. O emissor usa exatamente UM transporte por
 * par (recepção ativa nos dois) — a troca nunca perde nem duplica mensagem.
 * STUN público, sem TURN: NAT simétrico dos dois lados cai no relay e segue
 * jogável.
 */

export interface PeerInfo {
  readonly peerId: string
  readonly slot: number
  readonly nick: string
}

export interface RoomInfo {
  readonly code: string
  readonly peerId: string
  readonly slot: number
  readonly mode: string
  /** Mutável: o elenco muda com peerjoined/peerleft. */
  peers: PeerInfo[]
}

type GameMessage = Record<string, unknown>

const STUN_SERVERS: RTCIceServer[] = [{ urls: ['stun:stun.l.google.com:19302'] }]
/** DataChannel que não abriu em 8 s fica no relay (§13.3). */
const RTC_OPEN_TIMEOUT_MS = 8000

interface PairState {
  pc: RTCPeerConnection
  dc: RTCDataChannel | null
  open: boolean
  /** ICE que chegou antes da remoteDescription (trickle). */
  pendingIce: RTCIceCandidateInit[]
  hasRemote: boolean
}

export class NetLink {
  private ws: WebSocket | null = null
  private pending = new Map<string, (message: GameMessage) => void>()

  /** Preenchido após create/join. */
  room: RoomInfo | null = null

  /** Canais P2P por peer remoto (§13.3). */
  private pairs = new Map<string, PairState>()

  /**
   * Ida-e-volta medido até cada peer, em ms, suavizado por média móvel.
   *
   * Existe porque a previsão do guest precisava adivinhar quanto atraso
   * cobrir, e adivinhava errado: ela usava um valor fixo do tamanho do
   * amortecedor de jitter, que cobre a FILA mas não o trânsito. Numa partida
   * pela internet sobrava a viagem inteira descoberta, e o avião do
   * convidado respondia tarde.
   *
   * Suavizado porque a rede oscila: reagir a cada amostra faria a
   * profundidade de previsão pular de frame a frame, trocando um atraso
   * constante (que o jogador compensa) por um atraso instável (que não).
   */
  private readonly rtt = new Map<string, number>()
  private pingTimer: ReturnType<typeof setInterval> | undefined

  onMessage: (from: string, d: GameMessage) => void = () => {}
  onPeerJoined: (peer: PeerInfo) => void = () => {}
  onPeerLeft: (peerId: string, slot: number, wasHost: boolean) => void = () => {}
  onClose: () => void = () => {}

  static defaultUrl(): string {
    const override = new URLSearchParams(location.search).get('ws')
    if (override !== null) return override
    if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') {
      return 'ws://127.0.0.1:3021'
    }
    // Produção: mesmo domínio, atrás do nginx (§20).
    return `wss://${location.host}/pixelraid-ws/`
  }

  connect(url = NetLink.defaultUrl()): Promise<void> {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(url)
      this.ws = ws
      ws.onopen = () => resolve()
      ws.onerror = () => reject(new Error('não foi possível conectar ao servidor'))
      ws.onclose = () => this.onClose()
      ws.onmessage = (event) => this.route(JSON.parse(String(event.data)) as GameMessage)
    })
  }

  private route(message: GameMessage): void {
    const type = String(message.t)
    const waiter = this.pending.get(type)
    if (waiter !== undefined) {
      this.pending.delete(type)
      waiter(message)
      return
    }
    switch (type) {
      case 'msg': {
        const from = String(message.from)
        const d = message.d as GameMessage
        // Sinalização WebRTC é assunto interno do NetLink.
        if (typeof d?.k === 'string' && d.k.startsWith('rtc-')) {
          void this.handleSignal(from, d)
          return
        }
        this.deliver(from, d)
        return
      }
      case 'peerjoined': {
        const peer: PeerInfo = {
          peerId: String(message.peerId),
          slot: Number(message.slot),
          nick: String(message.nick),
        }
        this.room?.peers.push(peer)
        this.onPeerJoined(peer)
        return
      }
      case 'peerleft': {
        const peerId = String(message.peerId)
        if (this.room !== null) this.room.peers = this.room.peers.filter((p) => p.peerId !== peerId)
        this.onPeerLeft(peerId, Number(message.slot), message.wasHost === true)
        return
      }
      default:
        return
    }
  }

  private waitFor(types: string[], timeoutMs = 5000): Promise<GameMessage> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        for (const type of types) this.pending.delete(type)
        reject(new Error('tempo esgotado falando com o servidor'))
      }, timeoutMs)
      for (const type of types) {
        this.pending.set(type, (message) => {
          clearTimeout(timer)
          for (const other of types) this.pending.delete(other)
          resolve(message)
        })
      }
    })
  }

  private raw(payload: GameMessage): void {
    this.ws?.send(JSON.stringify(payload))
  }

  async createRoom(mode: 'coop' | 'race', nick: string): Promise<RoomInfo> {
    const reply = this.waitFor(['created', 'error'])
    this.raw({ t: 'create', mode, nick })
    const message = await reply
    if (message.t === 'error') throw new Error(String(message.reason))
    this.room = {
      code: String(message.code),
      peerId: String(message.peerId),
      slot: 0,
      mode: String(message.mode),
      peers: [{ peerId: String(message.peerId), slot: 0, nick }],
    }
    this.startPing()
    return this.room
  }

  async joinRoom(code: string, nick: string): Promise<RoomInfo> {
    const reply = this.waitFor(['start', 'error'])
    this.raw({ t: 'join', code, nick })
    const message = await reply
    if (message.t === 'error') throw new Error(String(message.reason))
    const others = (message.peers as PeerInfo[] | undefined) ?? []
    this.room = {
      code,
      peerId: String(message.peerId),
      slot: Number(message.slot),
      mode: String(message.mode),
      peers: [...others, { peerId: String(message.peerId), slot: Number(message.slot), nick }],
    }
    this.startPing()
    return this.room
  }

  /**
   * Envia `d` a todos os outros (sem `to`) ou a um peer específico.
   *
   * POR PAR: DataChannel aberto ⇒ P2P; senão ⇒ relay DIRECIONADO àquele
   * peer. Nunca broadcast de relay quando há pares em P2P — o emissor
   * escolhe UM transporte por destinatário, e ninguém recebe em dobro.
   */
  send(d: GameMessage, to?: string): void {
    const targets =
      to !== undefined
        ? [to]
        : (this.room?.peers ?? []).filter((p) => p.peerId !== this.room?.peerId).map((p) => p.peerId)
    for (const target of targets) {
      const pair = this.pairs.get(target)
      if (pair !== undefined && pair.open && pair.dc !== null && pair.dc.readyState === 'open') {
        pair.dc.send(JSON.stringify(d))
      } else {
        this.raw({ t: 'msg', to: target, d })
      }
    }
  }

  /**
   * Entrega uma mensagem de jogo, tirando do caminho o que é medição.
   *
   * Ping e pong NÃO sobem para a cena: são infraestrutura do transporte, e
   * quem consome `onMessage` é a lógica de partida. Passa pelos dois
   * back-ends (relay e DataChannel) porque o RTT que interessa é o do canal
   * que a partida realmente usa — medir pelo relay enquanto o jogo corre em
   * P2P daria um número certo sobre a rota errada.
   */
  private deliver(from: string, d: GameMessage): void {
    if (d?.k === 'pr-ping') {
      this.send({ k: 'pr-pong', ts: d.ts }, from)
      return
    }
    if (d?.k === 'pr-pong') {
      const amostra = performance.now() - Number(d.ts)
      if (!Number.isFinite(amostra) || amostra < 0) return
      const anterior = this.rtt.get(from)
      this.rtt.set(from, anterior === undefined ? amostra : anterior * 0.8 + amostra * 0.2)
      return
    }
    this.onMessage(from, d)
  }

  private startPing(): void {
    if (this.pingTimer !== undefined) return
    // 500 ms: a previsão precisa de uma MÉDIA, não de vigilância. Medir por
    // tick custaria 60 pacotes por segundo para refinar um número que muda em
    // escala de segundos.
    this.pingTimer = setInterval(() => {
      this.send({ k: 'pr-ping', ts: performance.now() })
    }, 500)
  }

  /**
   * Ida-e-volta até o anfitrião (slot 0), em ms, ou `null` enquanto nenhuma
   * amostra voltou. `null` é honesto: quem chama decide o que fazer sem
   * medida, em vez de receber um zero que parece rede perfeita.
   */
  rttToHost(): number | null {
    const host = this.room?.peers.find((p) => p.slot === 0)
    if (host === undefined || host.peerId === this.room?.peerId) return null
    return this.rtt.get(host.peerId) ?? null
  }

  /** Resumo do transporte para HUD/debug: pares abertos / total. */
  transport(): { open: number; total: number; label: string } {
    let open = 0
    for (const pair of this.pairs.values()) if (pair.open) open++
    const total = Math.max(0, (this.room?.peers.length ?? 1) - 1)
    return { open, total, label: total > 0 && open === total ? 'P2P' : open > 0 ? 'MISTO' : 'RELAY' }
  }

  /**
   * Dispara a negociação P2P com o elenco FECHADO (chamar na largada).
   * Determinístico: em cada par, o lado de MENOR peerId oferece.
   */
  startP2P(): void {
    const room = this.room
    if (room === null || typeof RTCPeerConnection === 'undefined') return
    for (const peer of room.peers) {
      if (peer.peerId === room.peerId || this.pairs.has(peer.peerId)) continue
      const initiator = room.peerId < peer.peerId
      const pair = this.createPair(peer.peerId, initiator)
      this.pairs.set(peer.peerId, pair)
    }
  }

  private createPair(remoteId: string, initiator: boolean): PairState {
    const pc = new RTCPeerConnection({ iceServers: STUN_SERVERS })
    const pair: PairState = { pc, dc: null, open: false, pendingIce: [], hasRemote: false }

    pc.onicecandidate = (event) => {
      if (event.candidate !== null) {
        this.raw({ t: 'msg', to: remoteId, d: { k: 'rtc-ice', cand: event.candidate.toJSON() } })
      }
    }
    pc.oniceconnectionstatechange = () => {
      const state = pc.iceConnectionState
      if (state === 'failed' || state === 'closed' || state === 'disconnected') {
        // Volta ao relay por par; recuperação de 'disconnected' transitório
        // reabre sozinha se o canal seguir vivo (dc.onopen já disparou).
        if (state !== 'disconnected') pair.open = false
      }
    }

    const wire = (dc: RTCDataChannel): void => {
      pair.dc = dc
      dc.onopen = () => {
        pair.open = true
      }
      dc.onclose = () => {
        pair.open = false
      }
      dc.onerror = () => {
        pair.open = false
      }
      dc.onmessage = (event) => {
        // Recepção ativa nos DOIS back-ends: quem fala escolhe o canal.
        this.deliver(remoteId, JSON.parse(String(event.data)) as GameMessage)
      }
    }

    if (initiator) {
      wire(pc.createDataChannel('game'))
      void pc
        .createOffer()
        .then(async (offer) => {
          await pc.setLocalDescription(offer)
          this.raw({ t: 'msg', to: remoteId, d: { k: 'rtc-offer', sdp: offer.sdp } })
        })
        .catch(() => {})
    } else {
      pc.ondatachannel = (event) => wire(event.channel)
    }

    // Timeout de abertura (§13.3): sem canal em 8 s, o relay segue titular.
    setTimeout(() => {
      if (!pair.open) {
        // Não fecha o pc: um open tardio ainda é bem-vindo; só não bloqueia.
      }
    }, RTC_OPEN_TIMEOUT_MS)

    return pair
  }

  private async handleSignal(from: string, d: GameMessage): Promise<void> {
    let pair = this.pairs.get(from)
    if (pair === undefined) {
      // Oferta chegou antes do nosso startP2P: cria o lado que responde.
      pair = this.createPair(from, false)
      this.pairs.set(from, pair)
    }
    try {
      if (d.k === 'rtc-offer') {
        await pair.pc.setRemoteDescription({ type: 'offer', sdp: String(d.sdp) })
        pair.hasRemote = true
        for (const cand of pair.pendingIce.splice(0)) await pair.pc.addIceCandidate(cand)
        const answer = await pair.pc.createAnswer()
        await pair.pc.setLocalDescription(answer)
        this.raw({ t: 'msg', to: from, d: { k: 'rtc-answer', sdp: answer.sdp } })
      } else if (d.k === 'rtc-answer') {
        await pair.pc.setRemoteDescription({ type: 'answer', sdp: String(d.sdp) })
        pair.hasRemote = true
        for (const cand of pair.pendingIce.splice(0)) await pair.pc.addIceCandidate(cand)
      } else if (d.k === 'rtc-ice') {
        const cand = d.cand as RTCIceCandidateInit
        if (pair.hasRemote) await pair.pc.addIceCandidate(cand)
        else pair.pendingIce.push(cand)
      }
    } catch {
      // Sinalização quebrada neste par: o relay continua titular.
      pair.open = false
    }
  }

  leave(): void {
    this.raw({ t: 'leave' })
    this.room = null
  }

  close(): void {
    if (this.pingTimer !== undefined) {
      clearInterval(this.pingTimer)
      this.pingTimer = undefined
    }
    this.rtt.clear()
    for (const pair of this.pairs.values()) {
      pair.dc?.close()
      pair.pc.close()
    }
    this.pairs.clear()
    this.ws?.close()
    this.ws = null
    this.room = null
  }
}
